import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  setDoc,
  doc, 
  updateDoc, 
  deleteDoc,
  query, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { db, auth } from './firebase';
import {
  generateAdminServiceQuoteEmail,
  generateClientServiceQuoteConfirmation,
  generateAdminWebsiteOnboardingEmail,
  generateClientWebsiteOnboardingConfirmation,
  EmailTemplateConfig,
  DEFAULT_EMAIL_CONFIG,
  setActiveEmailConfig
} from './emailTemplates';

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  };
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null): never {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

// Master administrator inboxes for all quote and project notifications
export const ADMIN_NOTIFICATION_RECIPIENTS = [
  'help@randonbrown.com',
  'randon.brown.it@gmail.com'
];

export interface ServiceQuoteInput {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  location: string;
  zipCode: string;
  addressType: 'business' | 'home';
  address: string;
  serviceMode: 'onsite' | 'remote' | 'pickup';
  availability: string;
  urgency: string;
  whenNeeded: string;
  problem: string;
  clientSignature: string;
  signatureDate: string;
  turnstileToken?: string;
  source?: 'web_form' | 'chat_popup';
  inquiryType?: string;
}

export interface ChatInquiryInput {
  name: string;
  email: string;
  phone?: string;
  topic?: string;
  message: string;
}

export interface ServiceQuoteRecord extends ServiceQuoteInput {
  id: string;
  status: 'new' | 'contacted' | 'in_progress' | 'completed' | 'archived';
  createdAt: string;
  adminResponse?: string;
  zohoEstimateNumber?: string;
  zohoAttachedAt?: string;
}

export interface WebsiteOnboardingInput {
  firstName: string;
  lastName: string;
  contactNumber: string;
  email: string;
  city: string;
  zipCode: string;
  businessName: string;
  domainName: string;
  domainStatus?: 'already_own' | 'need_purchase';
  domainScenario?: 'scenario_a_delegation' | 'scenario_b_cname' | 'scenario_c_new' | string;
  currentRegistrar?: string;
  hasBusinessEmail?: boolean;
  businessEmailProvider?: string;
  hostingPreference?: 'randon_managed' | 'client_controlled';
  deploymentPath?: 'path_a_github_actions' | 'path_b_agency_edge' | 'path_c_manual' | string;
  cloudflareAccountId?: string;
  editingPreference?: 'hands_off' | 'self_managed';
  tagline: string;
  address: string;
  hours: string;
  socialLinks: string;
  corePages: string;
  brandColors: string;
  cloudFolderLink: string;
  needsLogo?: 'have_logo' | 'need_logo' | 'text_only';
  logoNotes?: string;
  preferredLayout?: 'classic_topbar' | 'modern_centered' | 'lead_engine' | 'sidebar_nav' | string;
  layoutNotes?: string;
  leadOption: 'form' | 'web3forms' | 'email' | 'crm' | 'no-form' | string;
  formFields?: string[];
  customFormFields?: string;
  monthlySubmissions?: string;
  selectedPackage?: 'package-a' | 'package-b' | string;
  githubUsername?: string;
  stagedFileNames: string[];
  clientSignature: string;
  signatureDate: string;
  turnstileToken?: string;
}

export interface WebsiteOnboardingRecord extends WebsiteOnboardingInput {
  id: string;
  status: 'new' | 'contacted' | 'in_progress' | 'completed' | 'archived';
  createdAt: string;
  adminResponse?: string;
  zohoEstimateNumber?: string;
  zohoAttachedAt?: string;
}

function isValidEmail(email: unknown): boolean {
  if (typeof email !== 'string') return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

// Recursively strips undefined values so Firestore addDoc/setDoc never throws
function sanitizeForFirestore<T extends Record<string, any>>(obj: T): Record<string, any> {
  const clean: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      if (value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        clean[key] = sanitizeForFirestore(value as Record<string, any>);
      } else {
        clean[key] = value;
      }
    }
  }
  return clean;
}

// Helper to securely trigger server-side Resend email delivery
async function dispatchServerEmail(payload: {
  to?: string[];
  replyTo?: string;
  subject: string;
  text: string;
  html: string;
  turnstileToken?: string;
  'cf-turnstile-response'?: string;
  action?: string;
  type?: 'admin_alert' | 'client_receipt';
  clientConfirmation?: {
    to: string[];
    replyTo?: string;
    subject: string;
    text: string;
    html: string;
  };
}): Promise<{ success: boolean; id?: string; error?: string }> {
  try {
    const res = await fetch('/api/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const result: any = await res.json();
    return result;
  } catch (err) {
    console.warn('Direct server email dispatch notice:', err);
    return { success: false, error: err instanceof Error ? err.message : 'Network error' };
  }
}

// Public Submission for Service Quotes
export async function createServiceQuote(data: ServiceQuoteInput): Promise<{ success: boolean; id?: string; error?: string }> {
  let docId = `REQ-${Date.now().toString(36).toUpperCase()}`;
  let firestoreSaved = false;

  try {
    const cleanPayload = sanitizeForFirestore(data);
    const docRef = await addDoc(collection(db, 'service_quotes'), {
      ...cleanPayload,
      status: 'new',
      createdAt: new Date().toISOString(),
      timestamp: serverTimestamp()
    });
    docId = docRef.id;
    firestoreSaved = true;
  } catch (fsErr) {
    console.warn('⚠️ [Firestore Notice] Direct client Firestore write bypassed; proceeding with secure server dispatch:', fsErr);
  }

  try {
    const adminTemplate = generateAdminServiceQuoteEmail(data, docId);
    const clientEmail = data.email?.trim();

    // 1. Dispatch Admin Alert & Client Confirmation through a single validated server-side call
    // Tokens are single-use per Turnstile specification, so both emails are batched after one verification
    const clientTemplate = isValidEmail(clientEmail) ? generateClientServiceQuoteConfirmation(data, docId) : null;
    const emailResult = await dispatchServerEmail({
      to: ADMIN_NOTIFICATION_RECIPIENTS,
      replyTo: isValidEmail(clientEmail) ? clientEmail : 'help@randonbrown.com',
      subject: adminTemplate.subject,
      text: adminTemplate.text,
      html: adminTemplate.html,
      turnstileToken: data.turnstileToken,
      'cf-turnstile-response': data.turnstileToken,
      action: 'contact',
      type: 'admin_alert',
      clientConfirmation: clientTemplate && isValidEmail(clientEmail) ? {
        to: [clientEmail],
        replyTo: 'help@randonbrown.com',
        subject: clientTemplate.subject,
        text: clientTemplate.text,
        html: clientTemplate.html,
      } : undefined
    });

    // 2. Enqueue to Firestore 'mail' collection if client write is open
    if (firestoreSaved) {
      try {
        await addDoc(collection(db, 'mail'), {
          to: ADMIN_NOTIFICATION_RECIPIENTS,
          replyTo: isValidEmail(clientEmail) ? clientEmail : 'help@randonbrown.com',
          message: {
            subject: adminTemplate.subject,
            text: adminTemplate.text,
            html: adminTemplate.html
          },
          createdAt: serverTimestamp()
        });
      } catch (mailErr) {
        console.warn('Mail collection enqueue notice:', mailErr);
      }
    }

    if (!firestoreSaved && (!emailResult || !emailResult.success)) {
      return {
        success: false,
        error: emailResult?.error || 'Unable to submit quote. Please check your internet connection or email help@randonbrown.com directly.'
      };
    }

    return { success: true, id: docId };
  } catch (err: unknown) {
    console.error('Error processing quote submission:', err);
    return { 
      success: firestoreSaved, 
      id: firestoreSaved ? docId : undefined,
      error: err instanceof Error ? err.message : 'Failed to submit quote request' 
    };
  }
}

// Public Submission for Bottom-Left Chat Pop-up Inquiries
export async function createChatInquiry(data: ChatInquiryInput): Promise<{ success: boolean; id?: string; error?: string }> {
  const nameParts = (data.name || 'Chat Visitor').trim().split(/\s+/);
  const firstName = nameParts[0] || 'Chat';
  const lastName = nameParts.slice(1).join(' ') || 'Visitor';

  return createServiceQuote({
    firstName,
    lastName,
    email: data.email.trim(),
    phone: data.phone?.trim() || 'Provided via Chat Pop-up',
    location: 'Online Chat Inquiry',
    zipCode: 'Regional',
    addressType: 'home',
    address: 'Submitted through Bottom-Left Chat Pop-up',
    serviceMode: 'onsite',
    availability: 'Flexible / As soon as possible',
    urgency: data.topic ? `Chat Topic: ${data.topic}` : 'Standard Chat Inquiry',
    whenNeeded: 'ASAP',
    problem: `[BOTTOM-LEFT CHAT INQUIRY]\nTopic: ${data.topic || 'General Inquiry'}\nContact: ${data.name} (${data.email}${data.phone ? ` / ${data.phone}` : ''})\n\nMessage:\n${data.message}`,
    clientSignature: data.name.trim() || 'Chat Client',
    signatureDate: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    source: 'chat_popup',
    inquiryType: data.topic || 'General Chat'
  });
}

// Public Submission for Website Onboarding
export async function createWebsiteOnboarding(data: WebsiteOnboardingInput): Promise<{ success: boolean; id?: string; error?: string }> {
  let docId = `ONB-${Date.now().toString(36).toUpperCase()}`;
  let firestoreSaved = false;

  try {
    const cleanPayload = sanitizeForFirestore(data);
    const docRef = await addDoc(collection(db, 'website_onboardings'), {
      ...cleanPayload,
      status: 'new',
      createdAt: new Date().toISOString(),
      timestamp: serverTimestamp()
    });
    docId = docRef.id;
    firestoreSaved = true;
  } catch (fsErr) {
    console.warn('⚠️ [Firestore Notice] Direct client Firestore onboarding write bypassed:', fsErr);
  }

  try {
    const adminTemplate = generateAdminWebsiteOnboardingEmail(data, docId);
    const clientEmail = data.email?.trim();
    const clientTemplate = isValidEmail(clientEmail) ? generateClientWebsiteOnboardingConfirmation(data, docId) : null;

    // Dispatch verified alert & client receipt through single server call
    const emailResult = await dispatchServerEmail({
      to: ADMIN_NOTIFICATION_RECIPIENTS,
      replyTo: isValidEmail(clientEmail) ? clientEmail : 'help@randonbrown.com',
      subject: adminTemplate.subject,
      text: adminTemplate.text,
      html: adminTemplate.html,
      turnstileToken: data.turnstileToken,
      'cf-turnstile-response': data.turnstileToken,
      action: 'contact',
      type: 'admin_alert',
      clientConfirmation: clientTemplate && isValidEmail(clientEmail) ? {
        to: [clientEmail],
        replyTo: 'help@randonbrown.com',
        subject: clientTemplate.subject,
        text: clientTemplate.text,
        html: clientTemplate.html,
      } : undefined
    });

    if (firestoreSaved) {
      try {
        await addDoc(collection(db, 'mail'), {
          to: ADMIN_NOTIFICATION_RECIPIENTS,
          replyTo: isValidEmail(clientEmail) ? clientEmail : 'help@randonbrown.com',
          message: {
            subject: adminTemplate.subject,
            text: adminTemplate.text,
            html: adminTemplate.html
          },
          createdAt: serverTimestamp()
        });
      } catch (mailErr) {
        console.warn('Mail collection enqueue notice:', mailErr);
      }
    }

    if (!firestoreSaved && (!emailResult || !emailResult.success)) {
      return {
        success: false,
        error: emailResult?.error || 'Unable to submit onboarding brief. Please email help@randonbrown.com directly.'
      };
    }

    return { success: true, id: docId };
  } catch (err: unknown) {
    console.error('Error saving onboarding to Firestore:', err);
    return { 
      success: firestoreSaved, 
      id: firestoreSaved ? docId : undefined,
      error: err instanceof Error ? err.message : 'Failed to save onboarding brief' 
    };
  }
}

// Admin fetchers with resilient index fallback and in-memory sorting
export async function fetchServiceQuotes(): Promise<ServiceQuoteRecord[]> {
  try {
    let docsList;
    try {
      const q = query(collection(db, 'service_quotes'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      docsList = snapshot.docs;
    } catch (orderErr) {
      console.warn('Fallback: querying service_quotes without orderBy:', orderErr);
      const snapshot = await getDocs(collection(db, 'service_quotes'));
      docsList = snapshot.docs;
    }

    const records = docsList.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as ServiceQuoteRecord[];

    return records.sort((a, b) => {
      const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return timeB - timeA;
    });
  } catch (err) {
    console.error('Error fetching service quotes:', err);
    handleFirestoreError(err, OperationType.LIST, 'service_quotes');
  }
}

export async function fetchWebsiteOnboardings(): Promise<WebsiteOnboardingRecord[]> {
  try {
    let docsList;
    try {
      const q = query(collection(db, 'website_onboardings'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      docsList = snapshot.docs;
    } catch (orderErr) {
      console.warn('Fallback: querying website_onboardings without orderBy:', orderErr);
      const snapshot = await getDocs(collection(db, 'website_onboardings'));
      docsList = snapshot.docs;
    }

    const records = docsList.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as WebsiteOnboardingRecord[];

    return records.sort((a, b) => {
      const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return timeB - timeA;
    });
  } catch (err) {
    console.error('Error fetching website onboardings:', err);
    handleFirestoreError(err, OperationType.LIST, 'website_onboardings');
  }
}

export async function updateServiceQuoteStatus(id: string, status: ServiceQuoteRecord['status']): Promise<boolean> {
  try {
    const quoteRef = doc(db, 'service_quotes', id);
    await updateDoc(quoteRef, { status });
    return true;
  } catch (err) {
    console.error('Error updating quote status:', err);
    return false;
  }
}

export async function updateWebsiteOnboardingStatus(id: string, status: WebsiteOnboardingRecord['status']): Promise<boolean> {
  try {
    const obRef = doc(db, 'website_onboardings', id);
    await updateDoc(obRef, { status });
    return true;
  } catch (err) {
    console.error('Error updating onboarding status:', err);
    return false;
  }
}

export async function updateServiceQuoteZohoData(
  id: string, 
  data: { adminResponse?: string; zohoEstimateNumber?: string; zohoAttachedAt?: string }
): Promise<boolean> {
  try {
    const quoteRef = doc(db, 'service_quotes', id);
    const cleanPayload = sanitizeForFirestore(data);
    await updateDoc(quoteRef, cleanPayload);
    return true;
  } catch (err) {
    console.error('Error updating service quote Zoho data:', err);
    return false;
  }
}

export async function updateWebsiteOnboardingZohoData(
  id: string, 
  data: { adminResponse?: string; zohoEstimateNumber?: string; zohoAttachedAt?: string }
): Promise<boolean> {
  try {
    const obRef = doc(db, 'website_onboardings', id);
    const cleanPayload = sanitizeForFirestore(data);
    await updateDoc(obRef, cleanPayload);
    return true;
  } catch (err) {
    console.error('Error updating website onboarding Zoho data:', err);
    return false;
  }
}

export async function deleteServiceQuote(id: string): Promise<boolean> {
  try {
    const quoteRef = doc(db, 'service_quotes', id);
    await deleteDoc(quoteRef);
    return true;
  } catch (err) {
    console.error('Error deleting quote:', err);
    return false;
  }
}

export async function deleteWebsiteOnboarding(id: string): Promise<boolean> {
  try {
    const obRef = doc(db, 'website_onboardings', id);
    await deleteDoc(obRef);
    return true;
  } catch (err) {
    console.error('Error deleting onboarding:', err);
    return false;
  }
}

// ==========================================
// Email Template Customization Persistence
// ==========================================

export async function fetchEmailTemplateSettings(): Promise<EmailTemplateConfig> {
  try {
    const settingsDoc = await getDoc(doc(db, 'settings', 'email_templates'));
    if (settingsDoc.exists()) {
      const data = settingsDoc.data() as Partial<EmailTemplateConfig>;
      const merged = { ...DEFAULT_EMAIL_CONFIG, ...data };
      setActiveEmailConfig(merged);
      return merged;
    }
  } catch (err) {
    console.warn('Could not read email template settings from Firestore, using cached/local:', err);
  }

  // Fallback to local storage or defaults
  if (typeof window !== 'undefined') {
    try {
      const local = localStorage.getItem('rb_email_template_config');
      if (local) {
        const parsed = JSON.parse(local);
        const merged = { ...DEFAULT_EMAIL_CONFIG, ...parsed };
        setActiveEmailConfig(merged);
        return merged;
      }
    } catch {}
  }

  return { ...DEFAULT_EMAIL_CONFIG };
}

export async function saveEmailTemplateSettings(config: EmailTemplateConfig): Promise<boolean> {
  try {
    setActiveEmailConfig(config);

    // Save to Firestore
    await setDoc(doc(db, 'settings', 'email_templates'), {
      ...config,
      updatedAt: new Date().toISOString(),
      timestamp: serverTimestamp()
    }, { merge: true });

    return true;
  } catch (err) {
    console.error('Error saving email template settings to Firestore:', err);
    // Even if Firestore fails, local storage is updated via setActiveEmailConfig
    return false;
  }
}
